import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl, resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";
import { MediaLibraryPickerV2, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";
import { PublicationDestinationV2 } from "./PublicationDestinationV2";

const CROP_ASPECT = "4/5";
const MAIN_FRAME_LABEL = "Основное изображение · 4:5";
const MOBILE_FRAME_LABEL = "Мобильное изображение · 4:5";
const BG = "#F4EBD8";
const EYEBROW = "#8A6A46";
const TEXT = "#2A1D14";
const INTRO = "#5B4326";
const ACCENT = "#A67B4A";
const QUOTE = "#3E2E20";

function normalizeImage(raw) {
  if (!raw || (!raw.media_id && !raw.url)) return null;
  return {
    media_id: raw.media_id || null,
    url: raw.url || null,
    alt: raw.alt || "",
    caption: raw.caption || "",
    focal_x: raw.focal_x ?? 0.5,
    focal_y: raw.focal_y ?? 0.5,
    zoom: raw.zoom ?? 1,
  };
}

let idSeq = 0;
function newId(prefix) {
  idSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${idSeq}`;
}

function deriveInitial(content) {
  const c = content || {};
  const fromExtra = c.extra && typeof c.extra === "object" && c.extra.historical_editorial
    ? c.extra.historical_editorial : null;
  const h = c.historical_editorial && typeof c.historical_editorial === "object"
    ? c.historical_editorial
    : (fromExtra && typeof fromExtra === "object" ? fromExtra : {});

  const legacyBody = typeof c.body === "string" ? c.body.trim() : "";
  const paragraphs = Array.isArray(h.text_blocks) && h.text_blocks.length
    ? h.text_blocks.map((b, i) => ({ id: b?.id || `hist-p-${i}`, text: b?.text || "" }))
    : legacyBody ? [{ id: newId("hist-p"), text: legacyBody }] : [{ id: newId("hist-p"), text: "" }];

  return {
    eyebrow: h.eyebrow ?? c.eyebrow ?? "Историческая справка",
    title: h.title ?? c.headline ?? "",
    subtitle: h.subtitle ?? c.lead ?? "",
    period: h.period ?? "",
    location: h.location ?? "",
    paragraphs,
    timeline: Array.isArray(h.timeline)
      ? h.timeline.map((row, i) => ({ id: row?.id || `hist-t-${i}`, date: row?.date || "", text: row?.text || "" }))
      : [],
    mainImage: normalizeImage(h.main_image || (c.hero_url ? { media_id: c.hero_media_id, url: c.hero_url } : null)),
    mobileImage: normalizeImage(h.mobile_image || (c.hero_mobile_url ? { media_id: c.hero_mobile_media_id, url: c.hero_mobile_url } : null)),
    quote: h.quote ?? c.quote ?? "",
    quoteSource: h.quote_source ?? c.signature ?? "",
  };
}

function PhotoField({ label, helper, image, uploading, onUpload, onLibrary, onCrop, onRemove, onAlt, onCaption }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] text-espresso/65">{label}</label>
      {helper && <p className="mb-2 text-xs text-espresso/50">{helper}</p>}
      {image?.url ? (
        <div className="flex items-start gap-3">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-espresso/10">
            <img src={resolveMediaUrl(image.url)} alt={image.alt || ""}
              className="h-full w-full object-cover"
              style={{ objectPosition: `${image.focal_x * 100}% ${image.focal_y * 100}%` }} />
          </div>
          <div className="flex-1 space-y-1.5">
            <input value={image.alt} onChange={(e) => onAlt(e.target.value)}
              placeholder="Описание изображения (alt)"
              className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs" />
            <input value={image.caption} onChange={(e) => onCaption(e.target.value)}
              placeholder="Подпись под изображением"
              className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs" />
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={onUpload} disabled={uploading}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-50">
                {uploading ? "Загрузка…" : "Заменить фото"}
              </button>
              <button type="button" onClick={onLibrary}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso">Из Медиатеки</button>
              <button type="button" onClick={onCrop}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso">Настроить кадр</button>
              <button type="button" onClick={onRemove}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70">Удалить фото</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onUpload} disabled={uploading}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50">
            {uploading ? "Загрузка…" : "Загрузить фото"}
          </button>
          <button type="button" onClick={onLibrary}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso">Из Медиатеки</button>
        </div>
      )}
    </div>
  );
}

function HistoricalImage({ image, maxHeight }) {
  if (!image?.url) return null;
  return (
    <figure>
      <div className="aspect-[4/5] overflow-hidden bg-[#E7DDCC]" style={{ maxHeight }}>
        <img src={resolveMediaUrl(image.url)} alt={image.alt || ""}
          className="h-full w-full" style={editorialImageStyle(image)} />
      </div>
      {image.caption && (
        <figcaption className="mt-3 max-w-[38ch] font-serif text-[12px] italic leading-[1.5]" style={{ color: INTRO }}>
          {image.caption}
        </figcaption>
      )}
    </figure>
  );
}

function HistoricalText({ eyebrow, title, subtitle, period, location, paragraphs, timeline, quote, quoteSource, mobile = false }) {
  return (
    <div>
      <div className="mb-4 text-[10px] uppercase tracking-[0.28em]" style={{ color: EYEBROW }}>
        {eyebrow || "Историческая справка"}
      </div>

      {(period || location) && (
        <div className="mb-4 flex flex-wrap gap-x-5 gap-y-1 text-[10px] uppercase tracking-[0.18em]" style={{ color: EYEBROW }}>
          {period && <span>Период · <span className="normal-case tracking-normal font-serif text-[13px]" style={{ color: QUOTE }}>{period}</span></span>}
          {location && <span>Место · <span className="normal-case tracking-normal font-serif text-[13px]" style={{ color: QUOTE }}>{location}</span></span>}
        </div>
      )}

      {title && (
        <div className="font-serif leading-[1.08]" style={{ color: TEXT, fontSize: mobile ? "28px" : "36px" }}>
          {title}
        </div>
      )}
      {subtitle && (
        <div className="mt-3 max-w-[52ch] font-serif text-[16px] italic leading-[1.55]" style={{ color: INTRO }}>
          {subtitle}
        </div>
      )}

      {(title || subtitle) && <div className="my-6 h-px w-16" style={{ backgroundColor: ACCENT }} />}

      <div className="space-y-4">
        {paragraphs.filter((p) => p.text.trim()).map((p) => (
          <p key={p.id} className="max-w-[62ch] whitespace-pre-wrap font-serif text-[16px] leading-[1.75]" style={{ color: TEXT }}>
            {p.text}
          </p>
        ))}
      </div>

      {timeline.some((row) => row.date.trim() || row.text.trim()) && (
        <div className="mt-8 border-l pl-5" style={{ borderColor: ACCENT }}>
          <div className="mb-4 text-[10px] uppercase tracking-[0.24em]" style={{ color: EYEBROW }}>Хронология</div>
          <div className="space-y-4">
            {timeline.filter((row) => row.date.trim() || row.text.trim()).map((row) => (
              <div key={row.id} className="grid grid-cols-[84px_1fr] gap-3">
                <div className="font-serif text-[14px]" style={{ color: QUOTE }}>{row.date}</div>
                <div className="font-serif text-[15px] leading-[1.55]" style={{ color: TEXT }}>{row.text}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {quote && (
        <div className="mt-8 border-l-2 pl-4" style={{ borderColor: ACCENT }}>
          <div className="max-w-[52ch] font-serif text-[17px] italic leading-[1.6]" style={{ color: QUOTE }}>
            «{String(quote).trim().replace(/^[«"]+|[»"]+$/g, "")}»
          </div>
          {quoteSource && (
            <div className="mt-2 text-[11px] uppercase tracking-[0.2em]" style={{ color: EYEBROW }}>— {quoteSource}</div>
          )}
        </div>
      )}
    </div>
  );
}

export function EditorialHistoricalV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput }) {
  const c = block.content || {};
  const initial = deriveInitial(c);

  const [eyebrow, setEyebrow] = useState(initial.eyebrow);
  const [title, setTitle] = useState(initial.title);
  const [subtitle, setSubtitle] = useState(initial.subtitle);
  const [period, setPeriod] = useState(initial.period);
  const [location, setLocation] = useState(initial.location);
  const [paragraphs, setParagraphs] = useState(initial.paragraphs);
  const [timeline, setTimeline] = useState(initial.timeline);
  const [mainImage, setMainImage] = useState(initial.mainImage);
  const [mobileImage, setMobileImage] = useState(initial.mobileImage);
  const [quote, setQuote] = useState(initial.quote);
  const [quoteSource, setQuoteSource] = useState(initial.quoteSource);

  const [librarySlot, setLibrarySlot] = useState(null);
  const [cropSlot, setCropSlot] = useState(null);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle");
  const mainInput = useRef(null);
  const mobileInput = useRef(null);

  useEffect(() => {
    const next = deriveInitial(block.content || {});
    setEyebrow(next.eyebrow);
    setTitle(next.title);
    setSubtitle(next.subtitle);
    setPeriod(next.period);
    setLocation(next.location);
    setParagraphs(next.paragraphs);
    setTimeline(next.timeline);
    setMainImage(next.mainImage);
    setMobileImage(next.mobileImage);
    setQuote(next.quote);
    setQuoteSource(next.quoteSource);
    setLibrarySlot(null);
    setCropSlot(null);
    setSaveState("idle");
  }, [block.id]);  const activeMobileImage = mobileImage?.url ? mobileImage : mainImage;
  const cropImage = cropSlot === "mobile" ? mobileImage : cropSlot === "main" ? mainImage : null;

  const updateParagraph = (i, value) => setParagraphs((rows) => rows.map((row, idx) => idx === i ? { ...row, text: value } : row));
  const removeParagraph = (i) => setParagraphs((rows) => rows.length > 1 ? rows.filter((_, idx) => idx !== i) : [{ id: newId("hist-p"), text: "" }]);
  const moveParagraph = (i, dir) => setParagraphs((rows) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return rows;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const updateTimeline = (i, field, value) => setTimeline((rows) => rows.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  const removeTimeline = (i) => setTimeline((rows) => rows.filter((_, idx) => idx !== i));
  const moveTimeline = (i, dir) => setTimeline((rows) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return rows;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const pickFromLibrary = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    const image = normalizeImage({ media_id: media.id, url });
    if (librarySlot === "mobile") setMobileImage(image);
    else setMainImage(image);
    setLibrarySlot(null);
  };

  const upload = async (file, slot) => {
    if (!file) return;
    const setUploading = slot === "mobile" ? setUploadingMobile : setUploadingMain;
    const setImage = slot === "mobile" ? setMobileImage : setMainImage;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MEDIA_UPLOAD, formData, { headers: { "Content-Type": "multipart/form-data" } });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      setImage(normalizeImage({ media_id: data.id, url }));
      toast.success("Фото загружено");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const saveCrop = (values) => {
    if (cropSlot === "mobile") setMobileImage((img) => img ? { ...img, ...values } : img);
    if (cropSlot === "main") setMainImage((img) => img ? { ...img, ...values } : img);
    setCropSlot(null);
  };

  const save = async () => {
    setSaveState("saving");
    try {
      const historical = {
        eyebrow,
        title,
        subtitle,
        period,
        location,
        text_blocks: paragraphs.map((p) => ({ id: p.id, text: p.text })),
        timeline: timeline.map((row) => ({ id: row.id, date: row.date, text: row.text })),
        main_image: mainImage ? { ...mainImage } : null,
        mobile_image: mobileImage ? { ...mobileImage } : null,
        quote,
        quote_source: quoteSource,
      };
      const prevExtra = c.extra && typeof c.extra === "object" ? c.extra : {};
      const nextContent = {
        ...c,
        eyebrow,
        headline: title,
        lead: subtitle,
        quote,
        signature: quoteSource,
        media_ids: [mainImage?.media_id, mobileImage?.media_id].filter(Boolean),
        extra: {
          ...prevExtra,
          historical_editorial: historical,
        },
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent });
      setSaveState("saved");
      const savedMediaItems = [mainImage, mobileImage]
        .filter((img) => img?.media_id && img?.url)
        .map((img) => ({ id: img.media_id, url: img.url }));
      onSaved?.({ ...block, content: nextContent }, savedMediaItems);
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const saveLabel = { idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения" }[saveState];

  return (
    <div onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between border-b border-espresso/10 px-5 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Историческая справка</div>
            <div className="mt-0.5 font-serif text-xl text-espresso">{title || "Историческая справка"}</div>
          </div>
          {saveLabel && <span className="text-[11px] uppercase tracking-widest text-espresso/50">{saveLabel}</span>}
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <PublicationDestinationV2 materialId={block.material_id} volume={volume} />

          <PhotoField
            label="Основное изображение"
            image={mainImage}
            uploading={uploadingMain}
            onUpload={() => mainInput.current?.click()}
            onLibrary={() => setLibrarySlot("main")}
            onCrop={() => setCropSlot("main")}
            onRemove={() => setMainImage(null)}
            onAlt={(v) => setMainImage((img) => img ? { ...img, alt: v } : img)}
            onCaption={(v) => setMainImage((img) => img ? { ...img, caption: v } : img)}
          />
          <input ref={mainInput} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f, "main"); }} />

          <PhotoField
            label="Мобильное изображение · необязательно"
            helper="Если не добавлено, на телефоне используется основное изображение."
            image={mobileImage}
            uploading={uploadingMobile}
            onUpload={() => mobileInput.current?.click()}
            onLibrary={() => setLibrarySlot("mobile")}
            onCrop={() => setCropSlot("mobile")}
            onRemove={() => setMobileImage(null)}
            onAlt={(v) => setMobileImage((img) => img ? { ...img, alt: v } : img)}
            onCaption={(v) => setMobileImage((img) => img ? { ...img, caption: v } : img)}
          />
          <input ref={mobileInput} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f, "mobile"); }} />

          <div className="space-y-3">
            <input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)}
              placeholder="Метка раздела" className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Заголовок" className="w-full rounded-lg border border-espresso/20 px-3 py-2 font-serif text-lg" />
            <textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={2}
              placeholder="Короткий лид · необязательно"
              className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input value={period} onChange={(e) => setPeriod(e.target.value)}
                placeholder="Период · например XVIII–XIX века"
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
              <input value={location} onChange={(e) => setLocation(e.target.value)}
                placeholder="Место · например Генуя"
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-[11px] text-espresso/60">Текст</label>
                <button type="button" onClick={() => setParagraphs((rows) => [...rows, { id: newId("hist-p"), text: "" }])}
                  className="text-xs text-espresso/60 underline underline-offset-2">+ Добавить абзац</button>
              </div>
              <div className="space-y-2">
                {paragraphs.map((p, i) => (
                  <div key={p.id} className="rounded-lg border border-espresso/15 p-2">
                    <textarea value={p.text} onChange={(e) => updateParagraph(i, e.target.value)} rows={4}
                      placeholder="Абзац исторической справки"
                      className="w-full rounded-lg border border-espresso/20 px-2 py-1.5 text-sm" />
                    <div className="mt-1 flex gap-1">
                      <button type="button" onClick={() => moveParagraph(i, -1)} disabled={i === 0}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] disabled:opacity-30">▲</button>
                      <button type="button" onClick={() => moveParagraph(i, 1)} disabled={i === paragraphs.length - 1}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] disabled:opacity-30">▼</button>
                      <button type="button" onClick={() => removeParagraph(i)}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px]">Удалить</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <div>
                  <label className="text-[11px] text-espresso/60">Мини-хронология · необязательно</label>
                  <p className="text-[11px] text-espresso/45">Короткие даты или периоды, когда они действительно помогают истории.</p>
                </div>
                <button type="button" onClick={() => setTimeline((rows) => [...rows, { id: newId("hist-t"), date: "", text: "" }])}
                  className="text-xs text-espresso/60 underline underline-offset-2">+ Добавить</button>
              </div>
              {timeline.length > 0 && (
                <div className="space-y-2">
                  {timeline.map((row, i) => (
                    <div key={row.id} className="grid grid-cols-[110px_1fr_auto] gap-2 rounded-lg border border-espresso/15 p-2">
                      <input value={row.date} onChange={(e) => updateTimeline(i, "date", e.target.value)}
                        placeholder="1747" className="rounded-lg border border-espresso/20 px-2 py-1 text-xs" />
                      <input value={row.text} onChange={(e) => updateTimeline(i, "text", e.target.value)}
                        placeholder="Что произошло" className="rounded-lg border border-espresso/20 px-2 py-1 text-xs" />
                      <div className="flex gap-1">
                        <button type="button" onClick={() => moveTimeline(i, -1)} disabled={i === 0}
                          className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] disabled:opacity-30">▲</button>
                        <button type="button" onClick={() => moveTimeline(i, 1)} disabled={i === timeline.length - 1}
                          className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] disabled:opacity-30">▼</button>
                        <button type="button" onClick={() => removeTimeline(i)}
                          className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px]">×</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={2}
              placeholder="Цитата · необязательно"
              className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
            <input value={quoteSource} onChange={(e) => setQuoteSource(e.target.value)}
              placeholder="Источник / автор / издание · необязательно"
              className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
          </div>

          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")}
                    className={previewDevice === "desktop" ? "rounded-full bg-espresso px-2.5 py-1 text-cream" : "rounded-full px-2.5 py-1 text-espresso/60"}>Desktop</button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")}
                    className={previewDevice === "mobile" ? "rounded-full bg-espresso px-2.5 py-1 text-cream" : "rounded-full px-2.5 py-1 text-espresso/60"}>Mobile</button>
                </div>
              </div>

              {previewDevice === "desktop" ? (
                <div className="mx-auto p-5" style={{ backgroundColor: BG }}>
                  {mainImage?.url ? (
                    <div className="grid grid-cols-[minmax(0,42fr)_minmax(0,58fr)] gap-6">
                      <HistoricalText eyebrow={eyebrow} title={title} subtitle={subtitle} period={period} location={location}
                        paragraphs={paragraphs} timeline={timeline} quote={quote} quoteSource={quoteSource} />
                      <HistoricalImage image={mainImage} maxHeight={680} />
                    </div>
                  ) : (
                    <div className="mx-auto max-w-[720px]">
                      <HistoricalText eyebrow={eyebrow} title={title} subtitle={subtitle} period={period} location={location}
                        paragraphs={paragraphs} timeline={timeline} quote={quote} quoteSource={quoteSource} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="mx-auto max-w-[250px] p-4" style={{ backgroundColor: BG }}>
                  <HistoricalText eyebrow={eyebrow} title={title} subtitle={subtitle} period={period} location={location}
                    paragraphs={[]} timeline={[]} quote="" quoteSource="" mobile />
                  {activeMobileImage?.url && <div className="mt-6"><HistoricalImage image={activeMobileImage} maxHeight={420} /></div>}
                  <div className="mt-6">
                    <HistoricalText eyebrow="" title="" subtitle="" period="" location=""
                      paragraphs={paragraphs} timeline={timeline} quote={quote} quoteSource={quoteSource} mobile />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso">Закрыть</button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          <button type="button" onClick={save} disabled={saveState === "saving"}
            className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50">Сохранить изменения</button>
        </div>
      </div>

      <MediaLibraryPickerV2 open={librarySlot !== null} onClose={() => setLibrarySlot(null)} onPick={pickFromLibrary} />
      <EditorialCropV2
        open={cropSlot !== null}
        url={resolveMediaUrl(cropImage?.url || "")}
        aspectRatio={CROP_ASPECT}
        frameLabel={cropSlot === "mobile" ? MOBILE_FRAME_LABEL : MAIN_FRAME_LABEL}
        focalX={cropImage?.focal_x ?? 0.5}
        focalY={cropImage?.focal_y ?? 0.5}
        zoom={cropImage?.zoom ?? 1}
        onSave={saveCrop}
        onClose={() => setCropSlot(null)}
      />
    </div>
  );
}
